/* eslint-disable @typescript-eslint/no-explicit-any */
import { useContext } from 'react';
import { useClientEnvVarsStore } from '@marriott/mi-store-utils';
import { FEATURE_FLAGS, FLAG_TYPE } from '@marriott/mi-feature-flag-utils';

// Import the store and context
import {
  useSavedPropertiesDispatchStore,
  SavedPropertiesDispatchContextType,
} from '../../modules/store/savedPropertiesDispatchZustandStore';
import { SavedPropertiesDispatchContext } from '../../organisms/OSearchResults/searchResultsMainComponent';

/**
 * Hook that conditionally returns data from either the Zustand store or React Context
 * based on feature flags, maintaining the same API surface for seamless migration
 *
 * Uses feature flags: EXPERIENCE_MCOM_SHOP_ADF_ZUSTAND and EXPERIENCE_MCOM_SHOP_SERP_ZUSTAND
 *
 * Note: The Zustand store is updated proactively in searchResultsMainComponent where
 * the SavedPropertiesDispatchContext.Provider is set up, rather than reactively in this hook.
 * This is more efficient and follows the principle of updating at the source.
 */
export const useConditionalSavedPropertiesDispatchContext = (
  flagType: 'adf' | 'serp' = 'serp'
): SavedPropertiesDispatchContextType => {
  // Get feature flags from the client environment store
  const featureFlags = useClientEnvVarsStore(state => state.envVarsObject?.['featureFlags']) as unknown as Record<
    string,
    FLAG_TYPE
  >;

  // Determine which feature flag to check based on component type
  const shouldUseZustand =
    flagType === 'adf'
      ? featureFlags?.[FEATURE_FLAGS.EXPERIENCE_MCOM_SHOP_ADF_ZUSTAND] ?? false
      : featureFlags?.[FEATURE_FLAGS.EXPERIENCE_MCOM_SHOP_SERP_ZUSTAND] ?? false;

  // Always call hooks at top level (Rules of Hooks)
  const contextData = useContext(SavedPropertiesDispatchContext);
  const zustandStore = useSavedPropertiesDispatchStore();

  if (shouldUseZustand) {
    // Return Zustand store data (updated proactively in searchResultsMainComponent)
    return {
      savedData: zustandStore.savedData,
      dispatch: zustandStore.dispatch,
    };
  } else {
    // Return React Context data
    return contextData;
  }
};

/**
 * Convenience hook specifically for ADF components (AvailabilityCalendar, CalendarWrapper)
 * that checks EXPERIENCE_MCOM_SHOP_ADF_ZUSTAND flag for SavedPropertiesDispatchContext
 */
export const useConditionalSavedPropertiesDispatchContextADF = () => {
  return useConditionalSavedPropertiesDispatchContext('adf');
};

/**
 * Convenience hook specifically for SERP components
 * that checks EXPERIENCE_MCOM_SHOP_SERP_ZUSTAND flag for SavedPropertiesDispatchContext
 */
export const useConditionalSavedPropertiesDispatchContextSERP = () => {
  return useConditionalSavedPropertiesDispatchContext('serp');
};
